import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Keeps the Jobs page in Books fed (Oct 2, 2026). Renders nothing.
 *
 * Books cannot compute a job's per-square figures — the squares, the people and the commission rules live here —
 * so a finance-tier session computes them in the background and the database sends Books whatever changed
 * (lib/jobSnapshotPublish.ts). It runs for the same people as /financials, a little after the page they came for
 * has loaded, asks the server at most once an hour per browser, and the server says "not yet" unless the last
 * run is more than three hours old. A failure is logged to the console and never shown: this is housekeeping,
 * not something the person asked for.
 *
 * The heavy code (job math + its loaders) is imported only when a run is actually due, so nobody else's bundle grows.
 */
const FINANCE_ROLES = new Set(["admin", "finance", "sales_director"]);
// Same carve-out as /financials (financials_gate() refuses this address server-side too).
const EXCLUDED_EMAILS = new Set(["jcohan@bardinroofing.com"]);
const CHECKED_AT_KEY = "bp_books_job_sync_checked_at";
const CHECK_EVERY_MS = 60 * 60 * 1000;
const START_AFTER_MS = 20_000;

export default function BooksJobSync() {
  const { member, role, isSuperUser } = useAuth();
  const email = member?.email?.toLowerCase() ?? "";
  const allowed = !!member && !EXCLUDED_EMAILS.has(email) && (isSuperUser || (!!role && FINANCE_ROLES.has(role)));

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const last = Number(localStorage.getItem(CHECKED_AT_KEY) ?? 0);
          if (Number.isFinite(last) && Date.now() - last < CHECK_EVERY_MS) return;
          localStorage.setItem(CHECKED_AT_KEY, String(Date.now()));
          const { publishJobSnapshots } = await import("../lib/jobSnapshotPublish");
          if (cancelled) return;
          const res = await publishJobSnapshots();
          if (res && (res.sent > 0 || res.removed > 0 || res.unconfirmed > 0 || res.note)) {
            console.info(
              `[books job sync] ${res.total} jobs: ${res.sent} sent, ${res.unchanged} unchanged, ${res.removed} removed` +
                `${res.resent ? `, ${res.resent} sent again` : ""}${res.unconfirmed ? `, ${res.unconfirmed} not confirmed yet` : ""}${res.note ? ` — ${res.note}` : ""}`,
            );
          }
        } catch (e) {
          console.warn("[books job sync]", e instanceof Error ? e.message : e);
        }
      })();
    }, START_AFTER_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [allowed]);

  return null;
}
